import React, {FC, useEffect} from 'react';
import {connect} from 'react-redux';

import {ResolveDispatchProps} from '../../../../../../../src/helpers/reduxHelpers';
import {CrmSyncSourcesEnum} from '../../../../../../../src/models/api/crmSyncModel';
import {CollapsedItemKeyPrefixesEnum} from '../../../../../../../src/models/api/localPreferencesModel';
import {
  getAccountCrmSyncs,
  getIsCrmSyncConnectedById,
} from '../../../../../../../src/store/data/crmSync/crmSyncsSelectors';
import {
  getAccountsRunningImportJobs,
  getAllRunningSyncAccountsJobs,
} from '../../../../../../../src/store/data/jobs/jobsSelectors';
import {toggleExpandItemAction} from '../../../../../../../src/store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../../../../../../src/store/data/localPreferences/localPreferencesSelectors';
import {
  getHasCrmSyncFullPageSalesforce,
  getHasHubspotContactSync,
  getHasMicrosoftDynamicsAccountSync,
} from '../../../../../../../src/store/featureFlags/featureFlagsSelectors';
import {
  getContactsManagerTableState,
  makeGetAccountId,
  makeGetContactsManagerParameters,
} from '../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {
  openAccountDetailsAction,
  openCreateAccountFormAction,
} from '../../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelActions';
import {tableStateIsAccountsTableState} from '../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {ContactsManagerPanelId} from '../../../../../../../src/store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../../../../src/store/stateless/navigationActions';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {AccountsPage} from './accountsPage';

/*
 * Props.
 */

interface AccountsPageContainerPublicProps {
  panelId: ContactsManagerPanelId;
  shouldShowCrmSyncErrors?: boolean;
}

type AccountsPageContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type AccountsPageContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type AccountsPageContainerComponentProps = AccountsPageContainerPublicProps &
  AccountsPageContainerStateProps &
  AccountsPageContainerDispatchProps;

/*
 * Constants.
 */

const infoBoxExpandedKey = `${CollapsedItemKeyPrefixesEnum.ACCOUNTS_MANAGER}infoBox:v2`;
const isInfoBoxExpandedDefault = true;

/*
 * Selectors.
 */

function makeGetTotalResults() {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (!tableState || !tableStateIsAccountsTableState(tableState)) {
      return undefined;
    }
    return tableState.totalResults;
  });
}

const getIsInfoBoxExpanded = createSelector([getFindIsExpanded], (findIsExpanded) =>
  findIsExpanded(infoBoxExpandedKey, isInfoBoxExpandedDefault),
);

const getHasRunningImports = createSelector(
  [getAccountsRunningImportJobs],
  (accountJobs) => accountJobs.length > 0,
);

/*
 * Component.
 */

const AccountsPageContainerComponent: FC<AccountsPageContainerComponentProps> = (props) => {
  const {
    panelId,
    accountId,
    totalResults,
    hasRunningImports,
    isInfoBoxExpanded,
    activeSyncJobs,
    parameters,
    crmSyncs,
    shouldShowCrmSyncErrors,
    hasCrmSyncFullPageSalesforce,
    hasMicrosoftDynamicsAccountSync,
    hasHubspotContactSync,
    isCrmSyncConnectedById,
    openCreateAccountForm,
    onToggleInfoBox,
    openAccountDetails,
    navigateToLink,
  } = props;

  useEffect(() => {
    if (accountId) {
      openAccountDetails();
    }
  }, [accountId, openAccountDetails]);

  const isShowingImportSalesforceDialog =
    parameters.type === ContactsManagerParametersTypesEnum.ACCOUNT_SYNCS &&
    parameters.source === CrmSyncSourcesEnum.SALESFORCE;

  const isShowingImportHubspotDialog =
    parameters.type === ContactsManagerParametersTypesEnum.ACCOUNT_SYNCS &&
    parameters.source === CrmSyncSourcesEnum.HUBSPOT;

  return (
    <AccountsPage
      panelId={panelId}
      openCreateAccountForm={openCreateAccountForm}
      onToggleInfoBox={onToggleInfoBox}
      totalResults={totalResults}
      isInfoBoxExpanded={isInfoBoxExpanded}
      hasRunningImports={hasRunningImports}
      activeSyncJobs={activeSyncJobs}
      hasCrmSyncFullPageSalesforce={hasCrmSyncFullPageSalesforce}
      hasMicrosoftDynamicsAccountSync={hasMicrosoftDynamicsAccountSync}
      hasHubspotContactSync={hasHubspotContactSync}
      isShowingImportSalesforceDialog={isShowingImportSalesforceDialog}
      isShowingImportHubspotDialog={isShowingImportHubspotDialog}
      shouldShowCrmSyncErrors={shouldShowCrmSyncErrors}
      navigateToLink={navigateToLink}
      crmSyncs={crmSyncs}
      isCrmSyncConnectedById={isCrmSyncConnectedById}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AccountsPageContainerPublicProps>()(() => {
  const getAccountId = makeGetAccountId();
  return {
    accountId: getAccountId,
    totalResults: makeGetTotalResults(),
    isInfoBoxExpanded: getIsInfoBoxExpanded,
    hasRunningImports: getHasRunningImports,
    crmSyncs: getAccountCrmSyncs,
    parameters: makeGetContactsManagerParameters(),
    hasCrmSyncFullPageSalesforce: getHasCrmSyncFullPageSalesforce,
    hasMicrosoftDynamicsAccountSync: getHasMicrosoftDynamicsAccountSync,
    hasHubspotContactSync: getHasHubspotContactSync,
    activeSyncJobs: getAllRunningSyncAccountsJobs,
    isCrmSyncConnectedById: getIsCrmSyncConnectedById,
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  openCreateAccountForm: openCreateAccountFormAction,
  openAccountDetails: openAccountDetailsAction,
  navigateToLink: navigateToLinkAction,
  onToggleInfoBox: () => toggleExpandItemAction(infoBoxExpandedKey, isInfoBoxExpandedDefault),
}));

export const AccountsPageContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountsPageContainerComponent);
